import { type ReactElement, useEffect } from 'react';
import { trackUmamiEvent } from '@/helpers/analytics';
import {
  hasGamificationToastBeenDelivered,
  markGamificationToastDelivered,
} from '@/helpers/gamification-toast-session';
import { useAuth } from '@/hooks/auth';
import { useGamificationFeatureEnabled } from '@/hooks/data/config';
import { useMyGamification } from '@/hooks/data/gamification';
import { notify } from '@/services/notifications';
import { useProgressTrophiesModalStore } from '@/stores/progress-trophies-modal';

function toastIdForBatch(latestEventId: string): string {
  return `gamification-batch-${latestEventId}`;
}

/**
 * App-open coordinator: delivers at most one gamification toast per unseen batch
 * (keyed by latest_event_id) after auth hydration and a successful private query.
 * Toast view/dismiss never marks events seen — only modal open does (story 84-2).
 */
export function GamificationUpdateCoordinator(): ReactElement | null {
  const gamificationEnabled = useGamificationFeatureEnabled();
  const isAuthenticated = useAuth(state => state.isAuthenticated);
  const { data, isSuccess, isError, isLoading } = useMyGamification();
  const openForEvent = useProgressTrophiesModalStore(state => state.openForEvent);

  useEffect(() => {
    if (!gamificationEnabled || !isAuthenticated) {
      return;
    }

    if (!isSuccess || isError || isLoading || !data) {
      return;
    }

    const { has_unseen: hasUnseen, count, latest_event_id: latestEventId, hero_event: heroEvent } =
      data.updates;

    if (!hasUnseen || count <= 0 || !latestEventId) {
      return;
    }

    if (count === 1 && !heroEvent) {
      return;
    }

    if (hasGamificationToastBeenDelivered(latestEventId)) {
      return;
    }

    // Mark before show so Strict Mode remount / refetch cannot double-deliver.
    markGamificationToastDelivered(latestEventId);

    const toastId = toastIdForBatch(latestEventId);
    const eventType = heroEvent?.event_type ?? 'trophy_tier_unlocked';

    const handleView = (): void => {
      const tab = eventType === 'progress_tier_unlocked' ? 'progress' : 'trophies';
      trackUmamiEvent('gamification_toast_opened', { event_type: eventType });
      trackUmamiEvent('gamification_modal_open', { source: 'toast', tab, read_mode: 'own' });
      openForEvent(eventType);
    };

    if (count === 1 && heroEvent) {
      trackUmamiEvent('gamification_toast_shown', { kind: 'single', count: 1 });
      notify.gamification.single({
        toastId,
        title: heroEvent.title,
        body: heroEvent.body,
        action: {
          label: 'View',
          onClick: handleView,
        },
      });
      return;
    }

    trackUmamiEvent('gamification_toast_shown', { kind: 'summary', count });
    notify.gamification.summary(
      count,
      {
        label: 'View',
        onClick: handleView,
      },
      toastId,
    );
  }, [data, gamificationEnabled, isAuthenticated, isError, isLoading, isSuccess, openForEvent]);

  return null;
}
